import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@finitless/shared/hooks";
import { Breadcrumb, Icon } from "../../ui";
import { DeleteConfirmationModal } from "../../ui/Modals";
import {
  disconnectMetaAdsConnection,
  isMetaAdsApiConfigured,
} from "../../../hooks/metaAds/useMetaAdsApi";
import { isMetaAdsExperienceAvailable } from "../../../hooks/metaAds/useMetaAdsMode";
import { useMetaAdsGraphCampaigns } from "../../../hooks/metaAds/useMetaAdsSummary";
import {
  broadcastMetaAdsConnectionChanged,
  META_ADS_CONNECTION_STATUS_QUERY_KEY,
  useMetaAdsConnectionStatus,
} from "../../../hooks/metaAds/useMetaAdsConnectionStatus";
import { cn } from "@/lib/utils";
import {
  metaAdsFormSectionLabelClass,
  metaAdsPrimaryButtonClass,
  metaAdsTitleGradientClass,
} from "./metaAdsTheme";
import MetaAdsHubHeroVisual from "./MetaAdsHubHeroVisual";

const MetaAdsHubPage = () => {
  const { t } = useTranslation("metaAds");
  const { t: tNav } = useTranslation("navigation");
  const apiConfigured = isMetaAdsApiConfigured();
  const unlocked = isMetaAdsExperienceAvailable(apiConfigured);
  const {
    isConnected,
    isLoading: isConnectionLoading,
    adAccountId: connectionAdAccountId,
  } = useMetaAdsConnectionStatus();

  const { data: graphSummary } = useMetaAdsGraphCampaigns({
    enabled: Boolean(unlocked && isConnected),
    datePreset: "last_30d",
  });
  const adAccountId =
    graphSummary?.adAccountId ?? connectionAdAccountId ?? null;

  const [showAdAccountId, setShowAdAccountId] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);

  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const disconnectMutation = useMutation({
    mutationFn: disconnectMetaAdsConnection,
    onSuccess: () => {
      broadcastMetaAdsConnectionChanged();
      queryClient.invalidateQueries({
        queryKey: META_ADS_CONNECTION_STATUS_QUERY_KEY,
      });
      queryClient.invalidateQueries({ queryKey: ["meta-ads-graph-campaigns"] });
    },
    onError: (err) => {
      const detail = err instanceof Error ? err.message : "";
      showToast(
        detail
          ? `${t("hub.disconnectMetaError")}: ${detail}`
          : t("hub.disconnectMetaError"),
        "error",
      );
    },
  });

  const breadcrumbItems = useMemo(
    () => [
      { label: tNav("sidebar.growth"), disabled: true },
      {
        label: tNav("sidebar.metaAds"),
        active: true,
        disabled: true,
      },
    ],
    [tNav],
  );

  return (
    <div className="space-y-10">
      <DeleteConfirmationModal
        confirmButtonText={t("hub.disconnectMeta")}
        confirmLoadingText={t("hub.disconnectMetaBusy")}
        detailNote={t("hub.disconnectMetaModalDetail")}
        isLoading={disconnectMutation.isPending}
        isOpen={disconnectModalOpen}
        itemName={t("hub.disconnectMetaModalItemName")}
        itemType=""
        onClose={() => setDisconnectModalOpen(false)}
        onConfirm={() => {
          disconnectMutation.mutate(undefined, {
            onSuccess: () => setDisconnectModalOpen(false),
          });
        }}
        subtitle={t("hub.disconnectMetaModalSubtitle")}
        title={t("hub.disconnectMeta")}
      />

      <Breadcrumb items={breadcrumbItems} />

      <header className="space-y-3">
        <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
          <span className={metaAdsTitleGradientClass}>
            {t("hub.titleGradientWord")}
          </span>
          <span className="text-white">{t("hub.titleRest")}</span>
        </h1>
        <p className="text-base leading-relaxed text-white/55 sm:text-lg">
          {t("hub.heroSubtitle")}
        </p>

        {unlocked && isConnected ? (
          <div className="border-border/50 mt-1 flex flex-col gap-2 rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:p-3.5">
            <div className="min-w-0 flex-1 space-y-1">
              <p className={metaAdsFormSectionLabelClass}>
                {t("hub.adAccountLabel")}
              </p>
              {!adAccountId ? (
                <p className="text-sm leading-snug text-white/45">
                  {t("hub.adAccountLoadingHint")}
                </p>
              ) : showAdAccountId ? (
                <p
                  className="text-foreground font-mono text-sm leading-snug break-all sm:text-base"
                  id="meta-hub-ad-account-id"
                >
                  {adAccountId}
                </p>
              ) : (
                <p className="text-sm leading-snug text-white/45">
                  {t("hub.adAccountViewHint")}
                </p>
              )}
            </div>
            <div
              className={cn(
                "flex shrink-0 flex-wrap items-center gap-2 self-start",
                "sm:mt-0 sm:self-center",
              )}
            >
              {adAccountId ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowAdAccountId((v) => !v);
                  }}
                  className={cn(
                    "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 bg-white/[0.06] text-white/70 transition-colors",
                    "hover:border-white/25 hover:bg-white/10 hover:text-white",
                    showAdAccountId && "border-brand-cyan/40 text-brand-cyan",
                  )}
                  aria-pressed={showAdAccountId}
                  aria-expanded={showAdAccountId}
                  aria-controls={
                    showAdAccountId ? "meta-hub-ad-account-id" : undefined
                  }
                  aria-label={
                    showAdAccountId
                      ? t("hub.adAccountHideAria")
                      : t("hub.adAccountShowAria")
                  }
                >
                  <Icon
                    name={showAdAccountId ? "EyeOff" : "Eye"}
                    className="h-4 w-4"
                    aria-hidden
                  />
                </button>
              ) : null}
              <button
                type="button"
                disabled={!unlocked || disconnectMutation.isPending}
                onClick={() => setDisconnectModalOpen(true)}
                className={cn(
                  "inline-flex h-9 min-w-0 items-center justify-center rounded-lg border border-red-500/55 bg-transparent px-3 text-sm font-medium text-red-400 transition-colors",
                  "hover:border-red-400/65 hover:bg-red-950/45 hover:text-red-300",
                  "disabled:cursor-not-allowed disabled:opacity-45",
                  !unlocked && "pointer-events-none opacity-50",
                )}
              >
                {disconnectMutation.isPending
                  ? t("hub.disconnectMetaBusy")
                  : t("hub.disconnectMeta")}
              </button>
            </div>
          </div>
        ) : null}
      </header>

      {!unlocked && (
        <div
          className={cn(
            "rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100",
          )}
        >
          {t("hub.apiNotConfigured")}
        </div>
      )}

      {/* Primary CTA card — same surface as /orders (OrderCard + filter panels) */}
      <section
        className={cn(
          "overflow-hidden rounded-lg border border-gray-800 bg-gray-900",
        )}
      >
        <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-2 lg:items-center lg:gap-10 lg:p-10">
          <div className="min-w-0 space-y-4">
            <span
              className={cn(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-bold tracking-wider uppercase",
                isConnectionLoading
                  ? "border-white/20 bg-white/[0.06] text-white/50"
                  : isConnected
                    ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-300"
                    : "border-amber-500/40 bg-amber-500/[0.12] text-amber-100",
              )}
            >
              <span
                className={cn(
                  "h-2 w-2 rounded-full",
                  isConnectionLoading
                    ? "animate-pulse bg-white/40"
                    : isConnected
                      ? "bg-emerald-400"
                      : "bg-amber-400",
                )}
                aria-hidden
              />
              {isConnectionLoading
                ? t("hub.badgeMetaChecking")
                : isConnected
                  ? t("hub.badgeMetaConnected")
                  : t("hub.badgeMetaDisconnected")}
            </span>

            <h2 className="text-xl leading-snug font-bold text-white sm:text-2xl">
              {isConnectionLoading
                ? t("hub.cardTitleDisconnected")
                : isConnected
                  ? t("hub.cardTitleConnected")
                  : t("hub.cardTitleDisconnected")}
            </h2>
            <p className="text-sm leading-relaxed text-white/55 sm:text-[15px]">
              {isConnectionLoading
                ? t("hub.cardBodyChecking")
                : isConnected
                  ? t("hub.cardBodyConnected")
                  : t("hub.cardBodyDisconnected")}
            </p>

            <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:flex-wrap sm:items-center">
              {!isConnectionLoading && !isConnected ? (
                <Link
                  to="/growth/meta-ads/connect"
                  className={cn(
                    "inline-flex h-12 min-w-[140px] items-center justify-center rounded-xl px-8 text-base font-semibold",
                    metaAdsPrimaryButtonClass,
                    !unlocked && "pointer-events-none opacity-50",
                  )}
                >
                  {t("hub.ctaGetStarted")}
                </Link>
              ) : isConnectionLoading ? (
                <div
                  className="inline-flex h-12 min-w-[160px] items-center justify-center rounded-xl border border-gray-700 bg-gray-800/80 px-8 text-base font-medium text-white/45"
                  aria-busy="true"
                >
                  {t("hub.ctaChecking")}
                </div>
              ) : (
                <>
                  <Link
                    to="/growth/meta-ads/create"
                    className={cn(
                      "inline-flex h-12 min-w-[160px] items-center justify-center rounded-xl px-8 text-base font-semibold",
                      metaAdsPrimaryButtonClass,
                      !unlocked && "pointer-events-none opacity-50",
                    )}
                  >
                    {t("hub.ctaCreateCampaign")}
                  </Link>
                  <Link
                    to="/growth/meta-ads/performance"
                    className={cn(
                      "hover:bg-gray-750 inline-flex h-12 items-center justify-center rounded-lg border border-gray-700 bg-gray-800 px-6 text-base font-medium text-gray-300 transition-colors",
                      !unlocked && "pointer-events-none opacity-50",
                    )}
                  >
                    {t("hub.ctaViewPerformance")}
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-center lg:justify-end">
            <MetaAdsHubHeroVisual />
          </div>
        </div>
      </section>

      {/* Feature highlights — 3 columns */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[
          {
            icon: "Layers",
            title: t("hub.feature1Title"),
            body: t("hub.feature1Body"),
          },
          {
            icon: "Image",
            title: t("hub.feature2Title"),
            body: t("hub.feature2Body"),
          },
          {
            icon: "BarChart3",
            title: t("hub.feature3Title"),
            body: t("hub.feature3Body"),
          },
        ].map((f) => (
          <div
            key={f.title}
            className="flex h-full flex-col rounded-lg border border-gray-800 bg-gray-900 p-5 transition-colors hover:bg-gray-800/50"
          >
            <div className="mb-3 flex items-center gap-3 rounded-lg bg-gray-800 p-3">
              <Icon
                name={f.icon}
                className="text-brand-cyan h-5 w-5 shrink-0"
                aria-hidden
              />
              <h3 className="min-w-0 text-base leading-snug font-semibold text-white">
                {f.title}
              </h3>
            </div>
            <p className="text-sm leading-relaxed text-gray-400">{f.body}</p>
          </div>
        ))}
      </section>

      {/* Footer CTA — same card surface as orders pagination bar */}
      <section className="rounded-lg border border-gray-800 bg-gray-900 p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 sm:max-w-xl">
            <div className="text-brand-cyan flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gray-800">
              <Icon name="Rocket" className="h-5 w-5" aria-hidden />
            </div>
            <p className="text-sm leading-relaxed text-gray-400">
              {t("hub.strategyLine")}
            </p>
          </div>
          <Link
            to="/support"
            className="hover:bg-gray-750 inline-flex h-10 shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-gray-800 px-5 text-sm font-medium text-gray-200 transition-colors sm:ml-4"
          >
            {t("hub.talkToExpert")}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MetaAdsHubPage;
